import { ApolloLink, Observable, createHttpLink } from '@apollo/client/core'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import store from '@state/store'
import { accessToken, storedAccessToken } from '@utils/access-token'
import { reportError } from '@utils/report-error'
import { mutationSettled, mutationStarted } from '@utils/sw-update'
import { currentLocale } from '@src/plugins/i18n'
import { link } from './endpoint'

// Re-exported for the existing importers. New code that needs only the base URI
// should import from ./endpoint directly. Importing this module also builds the
// Apollo link chain below, which requires a global fetch.
export { link }

// Single chokepoint for every GraphQL/network failure. Previously these were
// logged at `console.debug` (invisible), so failing mutations died silently.
// Route them through `reportError` so they surface (and reach monitoring once
// wired). `operationName` makes it clear which query/mutation failed.
// Exported for unit testing. Routes every GraphQL and network failure through
// `reportError` (with the operation name) so nothing fails silently.
export const handleApolloError = ({
  graphQLErrors,
  networkError,
  operation,
}) => {
  const operationName = operation?.operationName
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      reportError(new Error(message), {
        source: 'graphql',
        operationName,
        locations,
        path,
      })
    })
  }
  if (networkError) {
    reportError(networkError, { source: 'network', operationName })
  }
}
const errorLink = onError(handleApolloError)

// Exported for unit testing. Attaches a live JWT as a Bearer token when the user
// has a session; otherwise leaves the request unauthenticated.
//
// Asks Amplify rather than reading the token straight out of Vuex. The stored copy
// is rewritten only by logIn and by the router guard's validate(), so a user who
// stayed on one page past the access token's lifetime (60 minutes by default) went
// on sending an expired JWT until something changed route. accessToken() refreshes
// when one is due and falls back to the stored token when Cognito is unreachable.
// see @utils/access-token.
export const authContext = async (_, { headers }) => {
  const token = await accessToken(storedAccessToken(store))
  if (!token) return
  return {
    headers: {
      ...headers,
      authorization: `Bearer ${token}`,
    },
  }
}
const authLink = setContext(authContext)

// Exported for unit testing. Sends the app's own language as Accept-Language, so
// the API words its refusals in it ("this control point is already locked out
// by..."): the language chosen in the app, not the browser's. Before it, every
// refusal reached a Spanish-speaking technician in English.
export const languageContext = (_, { headers }) => ({
  headers: {
    ...headers,
    'Accept-Language': currentLocale(),
  },
})
const languageLink = setContext(languageContext)

// Exported for unit testing. Retry transient failures but never retry a
// definitive outcome. A retry won't change it, and for auth failures it turns
// one rejection into five plus backoff. 401/403 are here because the backend
// now answers a missing or rejected token with 401 rather than 500; without
// them these would fall through to the retry branch.
// Whether an operation is a mutation. Shared by retryIf and the mutation
// tracker below so the two can never disagree about what a write is.
export const isMutationOperation = (operation) =>
  !!operation?.query?.definitions?.some(
    (def) => def.kind === 'OperationDefinition' && def.operation === 'mutation'
  )

export const retryIf = (error, operation) => {
  if (!error) return false

  // 422 is how the backend answers a request its schema rejects (a bad variable
  // type, an unknown input field). That verdict is final. Retrying it five
  // times with backoff only turned a fast, visible error into ~8 seconds of a
  // spinning Save button with nothing to show for it.
  const doNotRetryCodes = [400, 401, 403, 422, 500]
  if (doNotRetryCodes.includes(error.statusCode)) return false

  // Never re-send a MUTATION. A mutation is a write, and a network failure does
  // not tell us whether the server ran it: a timeout usually means it DID and is
  // still working. Re-sending duplicates the work.
  //
  // This was not theoretical. A timeout carries no statusCode, so it fell
  // through to "retry", and generateInfraredReport at RR/MTU - Aiken takes long
  // enough for the proxy to hang up: the retry started a SECOND report while the
  // first was still running, the two competed for bandwidth downloading the same
  // 344 photos, and each pass got slower, up to five attempts. Production logged
  // the same "fetched 344 exception photos" line twice, 27.6s and 1m10.4s, with
  // hundreds of "context canceled" from the abandoned attempt.
  //
  // Queries keep retrying: re-running a read is free and fixes a flaky network.
  if (isMutationOperation(operation)) return false

  return true
}

const retryLink = new RetryLink({
  delay: {
    initial: 100,
    max: 2000,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf,
  },
})

const httpLink = createHttpLink(link)

// Tracks MUTATIONS ONLY, so the auto-update logic never reloads mid-write. That is
// the one case that can lose data the user believes is already saved. Queries are
// deliberately not counted: a reload re-runs them harmlessly, and counting them
// would mean a polling page is never idle and so never updates.
//
// Placed at the FRONT of the chain so the count spans retries too (RetryLink can
// re-send a mutation up to 5 times); counting inside it would settle on the first
// attempt while a retry is still in flight.
const mutationTrackerLink = new ApolloLink((operation, forward) => {
  if (!isMutationOperation(operation)) return forward(operation)

  mutationStarted()
  let settled = false
  const settleOnce = () => {
    if (settled) return
    settled = true
    mutationSettled()
  }

  return new Observable((observer) => {
    const subscription = forward(operation).subscribe({
      next: (result) => observer.next(result),
      error: (networkError) => {
        settleOnce()
        observer.error(networkError)
      },
      complete: () => {
        settleOnce()
        observer.complete()
      },
    })
    // Unsubscribing (component torn down mid-flight) must settle too, or the count
    // leaks upward and the app stops auto-updating for the rest of the session.
    return () => {
      settleOnce()
      subscription.unsubscribe()
    }
  })
})

// NOTE: the 15s ApolloLinkTimeout from the Vue 2 stack was dropped. Its only
// build (apollo-link-timeout) depends on the legacy apollo-link v1 packages.
// Re-add a @apollo/client v3-compatible timeout link if request timeouts are needed.
export const apolloLink = ApolloLink.from([
  mutationTrackerLink,
  languageLink,
  authLink,
  retryLink,
  errorLink,
  httpLink,
])
